import React from 'react';
import styled from 'styled-components';
import { mediaQuery, DetailsBlock } from 'components';
import ContentLoader from 'react-content-loader';
import type { IContentLoaderProps } from 'react-content-loader';
import { AULink } from 'components/AULink';
import { Show } from 'components/Functional';

const cartItemPadding = 25;

export const CartItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  ${mediaQuery.medium`
    flex-direction: row;
  `}
`;

export const MobileArrangement = styled.div`
  width: 100%;
  ${mediaQuery.medium`
    display: none;
  `}
`;

export const CartItemContent = styled.div`
  display: flex;
  flex-direction: row;
`;

export const CartItemDetails = styled(DetailsBlock)`
  ${(props) => (props.showCustomOptionPrices || mediaQuery.medium ? `margin-left: ${cartItemPadding}px;` : '')};
  margin-bottom: 1rem;
  p {
    line-height: 1.2rem;
    &:last-of-type {
      margin-bottom: 0;
    }
  }
`;

export const MobileImageQtyPickerGrouping = styled.div`
  display: flex;
  flex-direction: column;
  ${mediaQuery.medium`
    display: none;
  `}
  img {
    margin-bottom: 10px;
    max-width: 8rem;
  }

  [class*='ImageWrap'] {
    min-height: 150px;
  }
`;

export const DesktopArrangement = styled.div`
  width: 100%;
  font-size: 1.25rem;
  display: none;
  ${mediaQuery.medium`
    display: flex;
  `}
`;

export const DesktopQtyPriceLinksGrouping = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  margin-left: auto;
`;

export const CartItemImageDetails = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
`;

export const DesktopQtyPrice = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

type PriceContainerProps = {
  isCartTransparencyTestOn: boolean;
  isDetailPrice?: boolean;
};

export const PriceContainer = styled.div<PriceContainerProps>`
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  font-family: ${(props) => (props.isCartTransparencyTestOn ? 'Lato' : 'Crimson Text, Georgia, serif')};
  font-size: ${(props) => (props.isCartTransparencyTestOn && props.isDetailPrice ? '12px' : '16px')};
  ${(props) => (props.isCartTransparencyTestOn && !props.isDetailPrice ? 'margin-top: 1.5rem;' : '')};
  font-weight: 400;
  line-height: 16px;
  margin-left: auto;
  padding-left: 5px;
  ${mediaQuery.medium`
    min-width: 135px;
    padding-left: 10px;
  `}
`;

export const ButtonList = styled.div`
  display: flex;
  justify-content: end;
  padding: ${cartItemPadding}px 1px 1px 1px;

  > button,
  > button a {
    cursor: pointer;
    text-decoration: underline;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    border: none;
    background-color: #ffffff;
    padding: 0;
    margin-left: 1rem;
    ${mediaQuery.medium`
      margin-left: 0.75rem;
    `}
    &:hover {
      color: #7c7c7c;
      text-decoration: underline;
    }
  }
`;
export const StyledCartItemList = styled.ul`
  display: flex;
  flex-direction: column;
  list-style-type: none;
  padding: 0;

  ${mediaQuery.medium`
    border-top: none;
    border-bottom: solid 1px #707070;
    margin-bottom: 3rem;
  `}

  > li {
    padding: 25px 0;
    border-top: solid 1px #707070;

    ${mediaQuery.medium`
      border-top: none;
      border-bottom: solid 1px #707070;
    `}
  }
`;

const CartItemListSkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 12px;
  width: 100%;
  svg {
    max-height: 125px;
    margin-bottom: 10px;
  }
`;

export interface CartItemLoaderProps extends IContentLoaderProps {
  screen?: string;
  imageOnly?: boolean;
  imageProps?: {
    x?: number;
    y?: number;
    rx?: number;
    ry?: number;
  };
}

export const CartItemLoader = ({
  screen,
  imageOnly = false,
  imageProps = {},
  ...contentLoaderProps
}: CartItemLoaderProps): React.ReactElement => {
  let height;

  switch (screen) {
    case 'mobile': {
      height = '100';
      break;
    }
    case 'desktop': {
      height = '100';
      break;
    }
    case 'large-screen': {
      height = '150';
      break;
    }
    default: {
      height = '100';
      break;
    }
  }

  return (
    <ContentLoader
      height={height}
      width="100%"
      speed={0.75}
      primaryColor="#f3f3f3"
      secondaryColor="#ecebeb"
      {...contentLoaderProps}
    >
      <rect x="20" y="20" rx="5" ry="5" width="100" height="100" {...imageProps} />
      <Show when={!imageOnly}>
        <rect x="130" y="20" rx="5" ry="5" width="600" height="15" />
        <rect x="130" y="50" rx="5" ry="5" width="380" height="15" />
        <rect x="130" y="80" rx="5" ry="5" width="225" height="15" />
        <rect x="650" y="110" rx="5" ry="5" width="75" height="15" />
      </Show>
    </ContentLoader>
  );
};

export const CartItemListSkeleton = (): React.JSX.Element => (
  <CartItemListSkeletonWrapper>
    {Array(3)
      .fill('')
      .map((e, i) => (
        <CartItemLoader
          height={125}
          width={800}
          screen="desktop"
          key={i}
          style={{ opacity: Number(2 / i).toFixed(1) }}
        />
      ))}
  </CartItemListSkeletonWrapper>
);

export const EmptyCartContent = styled.div`
  border-bottom: 1px solid #d2d2d0;
  margin-bottom: 3rem;
`;

export const ULink = styled(AULink)`
  text-decoration: underline;
`;
